<template>
    <div class="company-autocomplete__wrapper">
        <AutoComplete
            v-model.trim="currentCompany.company"
            :suggestions="searchingCompanies"
            :placeholder="$t('profile.new.experience.company')"
            :empty-search-message="$t('generic.no.results.found')"
            option-label="text"
            scroll-height="40vh"
            :delay="500"
            :auto-option-focus="false"
            :disabled="disabled"
            :invalid="!isCompanyAutocompleteValid || error"
            data-testid="add-experience-company-name"
            @complete="handleAutocompleteComplete"
            @update:model-value="handleAutocompleteUpdateModelValue"
            @keydown.escape.stop
        >
            <template #option="slotProps">
                <VJoyWrapper justify="flex-start" align="center" class="company-autocomplete__autocomplete-item">
                    <img
                        v-if="isNewCompanyLogoEnabled && slotProps.option.others.logoUrl"
                        width="32"
                        height="32"
                        :src="slotProps.option.others.logoUrl"
                        alt="company-logo"
                        style="object-fit: contain"
                    />
                    <MaltImg
                        v-else-if="slotProps.option.others.logoName"
                        fit="contain"
                        width="32"
                        height="32"
                        :src="companyLogoUrl(slotProps.option.others.logoBaseUrl, slotProps.option.others.logoName)"
                        alt="company-logo"
                    />
                    <VJoyIcon v-else name="company-placeholder" color="secondary" size="medium" />
                    <span>{{ slotProps.option.text }}</span>
                    <span v-if="slotProps.option.others.areacode">
                        {{ activityAreas.get(slotProps.option.others.areacode) }}
                    </span>
                </VJoyWrapper>
            </template>
        </AutoComplete>
    </div>
</template>
<script setup lang="ts">
    import {computed, ref} from 'vue';
    import AutoComplete from 'primevue/autocomplete';
    import {MaltImg} from '#components';
    import {useLogger} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {VJoyIcon, VJoyWrapper} from '@maltjoy/core-vue';
    import CompanySearcher from '~/components/edit/companies/CompanySearcher';
    import type {ActivityArea, Logo} from '~/openapi';
    import type {ActivityAreaResource} from '~/types/api-raw-types';
    import type {CompanySuggestion} from '~/types/domain/CompanySuggestions';

    export type CompanyAutocompleteModel = {
        companyId?: string;
        company: string;
        area?: ActivityAreaResource;
        logo?: Logo;
        logoUrl?: string;
    };

    const emit = defineEmits<{
        (event: 'update:modelValue', value: CompanyAutocompleteModel): void;
    }>();

    interface CompanyAutocompleteProps {
        modelValue: CompanyAutocompleteModel;
        isRequired: boolean;
        error: boolean;
        activityAreas: Map<ActivityArea, string>;
        disabled?: boolean;
    }

    const props = withDefaults(defineProps<CompanyAutocompleteProps>(), {
        disabled: false,
    });

    // Composables
    const logger = useLogger();
    const isNewCompanyLogoEnabled = useFeatureFlag('new-company-logo');

    // Refs
    const currentCompany = ref<CompanyAutocompleteModel>(props.modelValue);
    const searchingCompanies = ref();

    // Computed
    const isCompanyAutocompleteValid = computed(() => (props.isRequired ? !!currentCompany.value.company : true));

    function companyLogoUrl(baseUrl: string, logoName: string): string {
        return `${baseUrl}/${logoName}`;
    }

    const companiesSearcher = new CompanySearcher();
    async function handleAutocompleteComplete(event: any) {
        try {
            searchingCompanies.value = await companiesSearcher.search(event.query);
        } catch (error: any) {
            logger.error('handleAutocompleteComplete failed: ', error);
        }
    }

    function handleAutocompleteUpdateModelValue(companyNewValue: string | CompanySuggestion) {
        if (typeof companyNewValue === 'string') {
            currentCompany.value = {
                companyId: undefined,
                company: companyNewValue.trim(),
            };
        } else {
            currentCompany.value = {
                companyId: companyNewValue.key || undefined,
                company: companyNewValue.text.trim(),
                area: companyNewValue.others?.areacode as ActivityAreaResource,
                logo: {
                    baseUrl: companyNewValue.others?.logoBaseUrl,
                    name: companyNewValue.others?.logoName,
                },
                logoUrl: isNewCompanyLogoEnabled.value ? companyNewValue.others?.logoUrl : undefined,
            };
        }

        emit('update:modelValue', currentCompany.value);
    }
</script>
<style lang="scss" scoped>
    @import 'primeicons/primeicons.css';

    .company-autocomplete {
        &__wrapper {
            width: 100%;
        }

        &__autocomplete-item {
            --wrapper-gap: var(--joy-core-spacing-2);
            margin-bottom: 0;
        }
    }

    :deep(.p-inputtext.p-invalid::placeholder) {
        color: var(--joy-color-primary-30) !important;
    }
</style>
